import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { PositionedModal } from '@/common/components/PositionedModal';
import { Modal, Message } from 'semantic-ui-react';
import { StyledSaveButton, StyledCancelButton } from 'common/components/button/StyledButton';
import { Input } from '@/common/ui/input';
import { Label } from '@/common/ui/label';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/common/ui/select';
import { Autocomplete, AutocompleteOption } from '@/common/ui/autocomplete';
import {
  useCreateTeamMutation,
  useUpdateTeamMutation,
  useSetTeamLeaderMutation,
  useGetMyTeamsQuery,
  useGetAllTeamsQuery,
} from '../api/teamApi';
import { Team } from '../types';
import {
  isCurrentUserStaffOrSuperAdmin,
  isCurrentMemberOrgAdmin,
  isCurrentMemberDirector,
  getOrganizationMembers,
} from '@/common/selectors/auth';
import { isEmployeeStatusActive } from '../../settings/employee-management/utils/EmployeeUtils';
import styled from 'styled-components';
import { PageContent, Title } from '@/common/styles/page';

const FormGroup = styled.div`
  margin-bottom: 20px;
`;

const ColorInput = styled(Input)`
  width: 80px;
  height: 40px;
  padding: 4px;
  cursor: pointer;
`;

const ColorContainer = styled.div`
  display: flex;
  align-items: center;
`;

const PaletteContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 10px;
`;

const PaletteSwatch = styled.button<{ color: string; selected: boolean }>`
  width: 28px;
  height: 28px;
  border-radius: 6px;
  border: 2px solid ${({ selected }) => (selected ? '#111' : '#ccc')};
  background: ${({ color }) => color};
  cursor: pointer;
  padding: 0;
  transition: border-color 0.2s ease;

  &:focus {
    outline: 2px solid #2563eb;
    outline-offset: 2px;
  }
`;

const PROFESSIONAL_TEAM_COLORS = [
  '#264653',
  '#2A9D8F',
  '#4C6EB1',
  '#F4A261',
  '#E76F51',
  '#8E5A9B',
  '#7EB77F',
  '#FF6D60',
  '#5C7C8A',
  '#D9835A',
];

interface TeamEditModalProps {
  isOpen: boolean;
  onClose: () => void;
  team?: Team | null;
  onSuccess?: () => void;
  initialParentTeamId?: number | 'TOP_LEVEL' | null; // new: pre-select parent when creating a team
}

export const TeamEditModal: React.FC<TeamEditModalProps> = ({
  isOpen,
  onClose,
  team,
  onSuccess,
  initialParentTeamId,
}) => {
  const [name, setName] = useState('');
  const [color, setColor] = useState<string>('');
  const [externalId, setExternalId] = useState<string>('');
  const [parentTeamId, setParentTeamId] = useState<number | 'TOP_LEVEL' | null>(null);
  const [leaderId, setLeaderId] = useState<number | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [createTeam, { isLoading: isCreating }] = useCreateTeamMutation();
  const [updateTeam, { isLoading: isUpdating }] = useUpdateTeamMutation();
  const [setTeamLeader] = useSetTeamLeaderMutation();
  const { refetch } = useGetMyTeamsQuery();
  const { data: allTeamsData } = useGetAllTeamsQuery(undefined, { skip: !isOpen });

  const isStaffOrAdmin = useSelector(isCurrentUserStaffOrSuperAdmin);
  const isOrgAdmin = useSelector(isCurrentMemberOrgAdmin);
  const isDirector = useSelector(isCurrentMemberDirector);
  const organizationMembers = useSelector(getOrganizationMembers);
  const canUpdateTeam = Boolean(isStaffOrAdmin || isOrgAdmin || isDirector);

  const activeOrganizationMembers = React.useMemo(
    () => (organizationMembers || []).filter((member) => member.status == null || isEmployeeStatusActive(member.status as any)),
    [organizationMembers]
  );

  // Keep leader picker ordering stable by last name, then first name.
  const sortedOrganizationMembers = React.useMemo(
    () =>
      (activeOrganizationMembers || []).slice().sort((a, b) => {
        const aLast = (a.lastName || '').trim();
        const bLast = (b.lastName || '').trim();
        const lastNameCompare = aLast.localeCompare(bLast, undefined, { sensitivity: 'base' });
        if (lastNameCompare !== 0) {
          return lastNameCompare;
        }

        const aFirst = (a.firstName || '').trim();
        const bFirst = (b.firstName || '').trim();
        const firstNameCompare = aFirst.localeCompare(bFirst, undefined, { sensitivity: 'base' });
        if (firstNameCompare !== 0) {
          return firstNameCompare;
        }

        return (a.email || '').localeCompare(b.email || '', undefined, { sensitivity: 'base' });
      }),
    [activeOrganizationMembers]
  );

  // Convert organization members to autocomplete options
  const leaderOptions: AutocompleteOption[] = React.useMemo(() => [
    { value: '', label: 'No leader', sublabel: '' },
    ...sortedOrganizationMembers.map(member => ({
      value: member.id.toString(),
      label: (member.firstName || member.lastName)
        ? `${member.lastName}${member.firstName ? `, ${member.firstName}` : ''}`
        : member.email,
      sublabel: member.email,
    }))
  ], [sortedOrganizationMembers]);

  // Build a set of invalid parent IDs (descendants of the current team) to prevent cycles
  const availableParentOptions = React.useMemo(() => {
    const all = (allTeamsData?.results || []).slice();
    // Always return options sorted alphabetically for a stable UX
    all.sort((a, b) => (a.name || '').localeCompare(b.name || ''));

    if (!team) return all; // when creating, all teams are available (already sorted)

    // build children adjacency
    const children = new Map<number, number[]>();
    all.forEach(t => {
      if (t.parent_team != null && t.parent_team !== t.id) {
        const arr = children.get(t.parent_team) || [];
        arr.push(t.id);
        children.set(t.parent_team, arr);
      }
    });

    // collect descendants of current team
    const descendants = new Set<number>();
    const visit = (id: number) => {
      const ch = children.get(id) || [];
      ch.forEach(cid => {
        if (!descendants.has(cid)) {
          descendants.add(cid);
          visit(cid);
        }
      });
    };

    visit(team.id);

    // exclude the current team and its descendants
    return all.filter(t => t.id !== team.id && !descendants.has(t.id));
  }, [allTeamsData, team]);

  useEffect(() => {
    if (team) {
      setName(team.name);
      setColor(team.color || '');
      setExternalId(team.external_id || '');
      // If the team's parent is itself, treat that as a Top-level team
      if (team.parent_team === team.id) {
        setParentTeamId('TOP_LEVEL');
      } else {
        setParentTeamId(team.parent_team ?? null);
      }
      setLeaderId(team.leader_id || null);
    } else {
      setName('');
      setColor('');
      setExternalId('');
      // Use the initialParentTeamId when creating a new team
      setParentTeamId(initialParentTeamId ?? null);
      setLeaderId(null);
    }

    if (isOpen) {
      setSaveError(null);
    }
  }, [team, isOpen, initialParentTeamId]);

  const handleSubmit = async (e?: React.FormEvent) => {
    if (e) {
      e.preventDefault();
    }

    if (!name.trim()) {
      setSaveError('Team name is required');
      return;
    }

    try {
      if (team) {
        // validate selection to ensure no cycle
        if (parentTeamId && typeof parentTeamId === 'number') {
          const invalidIds = new Set(availableParentOptions.map(t => t.id));
          // if parentTeamId not in availableParentOptions and not TOP_LEVEL/null then it's invalid
          if (!invalidIds.has(parentTeamId)) {
            setSaveError('Invalid parent selected — this would create a cycle. Please choose another parent.');
            return;
          }
        }

        // For existing team, if TOP_LEVEL was selected we send the team's own id as parent
        const parentToSend = parentTeamId === 'TOP_LEVEL' ? team.id : parentTeamId ?? null;
        const payload = {
          id: team.id,
          name: name.trim(),
          color: color.trim() || null,
          external_id: externalId.trim() || null,
          parent_team: parentToSend,
          leader: leaderId,
        };

        const leaderChanged = leaderId !== (team.leader_id || null);

        if (canUpdateTeam) {
          await updateTeam(payload).unwrap();
        } else if (leaderChanged) {
          if (leaderId == null) {
            setSaveError('Only organization admins/directors can remove a team leader.');
            return;
          }

          await setTeamLeader({ teamId: team.id, employee_id: leaderId }).unwrap();
        } else {
          setSaveError('You do not have permission to edit this team.');
          return;
        }
      } else {
        // For new teams, if TOP_LEVEL was selected we first create with no parent, then PATCH to set parent to the created id
        const createPayload: any = {
          name: name.trim(),
          color: color.trim() || null,
          external_id: externalId.trim() || null,
          parent_team: parentTeamId === 'TOP_LEVEL' ? null : (parentTeamId ?? null),
          leader: leaderId,
        };

        const created = await createTeam(createPayload).unwrap();

        if (parentTeamId === 'TOP_LEVEL') {
          // Set its parent to itself to mark top-level
          await updateTeam({ id: created.id, parent_team: created.id }).unwrap();
        }
      }
      refetch();
      if (onSuccess) {
        await onSuccess();
      } else {
        onClose();
      }
    } catch (error: any) {
      console.error('Failed to save team:', error);

      const errorMessage =
        (Array.isArray(error?.data?.details?.name) && error.data.details.name[0]) ||
        (Array.isArray(error?.data?.name) && error.data.name[0]) ||
        (Array.isArray(error?.data?.details?.non_field_errors) && error.data.details.non_field_errors[0]) ||
        (Array.isArray(error?.data?.non_field_errors) && error.data.non_field_errors[0]) ||
        'Failed to save team. Please try again.';

      setSaveError(errorMessage);
    }
  };

  const isLoading = isCreating || isUpdating;

  return (
    <PositionedModal open={isOpen} onClose={onClose} size="small">
      <Modal.Header>
        <Title>{team ? 'Edit Team' : 'Create Team'}</Title>
      </Modal.Header>

      <Modal.Content>
        <form onSubmit={handleSubmit} id="team-form" autoComplete="off">
          {saveError && (
            <Message negative>
              <Message.Header>Unable to save team</Message.Header>
              <p>{saveError}</p>
            </Message>
          )}
          <FormGroup>
            <Label htmlFor="team-name">Team Name *</Label>
            <Input
              id="team-name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Enter team name"
              required
              disabled={isLoading}
            />
          </FormGroup>

          <FormGroup>
            <Label htmlFor="parent-team">Parent Team</Label>
            {/* 'TOP_LEVEL' represents selecting the team as a top-level parent (parent = self) */}
            <Select
              value={parentTeamId === 'TOP_LEVEL' ? 'TOP_LEVEL' : (parentTeamId ? parentTeamId.toString() : '')}
              onValueChange={(value) => {
                if (value === 'TOP_LEVEL') setParentTeamId('TOP_LEVEL');
                else setParentTeamId(value ? parseInt(value, 10) : null);
              }}
              disabled={isLoading}
            >
              <SelectTrigger disabled={isLoading}>
                <SelectValue>
                  {parentTeamId === 'TOP_LEVEL' ? 'Top-level team'
                    : parentTeamId
                      ? (allTeamsData?.results || []).find(t => t.id === parentTeamId as number)?.name || 'Parent'
                      : 'No parent'
                  }
                </SelectValue>
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="TOP_LEVEL">Top-level team</SelectItem>
                <SelectItem value="">No parent</SelectItem>
                {availableParentOptions.map((teamOpt) => (
                  <SelectItem key={teamOpt.id} value={teamOpt.id.toString()}>
                    {teamOpt.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </FormGroup>

          <FormGroup>
            <Label htmlFor="team-external-id">External ID</Label>
            <Input
              id="team-external-id"
              type="text"
              name="team-external-id"
              value={externalId}
              onChange={(e) => setExternalId(e.target.value)}
              placeholder="Optional external ID"
              autoComplete="off"
              autoCorrect="off"
              autoCapitalize="none"
              spellCheck={false}
              disabled={isLoading}
            />
          </FormGroup>

          <FormGroup>
            <Label htmlFor="team-leader">Team Leader</Label>
            <Autocomplete
              options={leaderOptions}
              value={leaderId?.toString() || ''}
              onChange={(value) => setLeaderId(value ? parseInt(value, 10) : null)}
              placeholder="Search for team leader..."
              disabled={isLoading}
              noOptionsMessage="No members found"
            />
          </FormGroup>

          <FormGroup>
            <Label htmlFor="team-color">Team Color</Label>
            <ColorContainer>
              <ColorInput
                id="team-color"
                type="color"
                value={color || '#ffffff'}
                onChange={(e) => setColor(e.target.value)}
                disabled={isLoading}
                aria-label="Pick team color"
                data-testid="team-color-picker"
              />
              <Input
                type="text"
                value={color}
                onChange={(e) => setColor(e.target.value)}
                placeholder="Click the square to select a color"
                style={{ marginLeft: '8px', flex: 1 }}
                disabled={isLoading}
              />
            </ColorContainer>
            <PaletteContainer>
              {PROFESSIONAL_TEAM_COLORS.map((colorOption) => (
                <PaletteSwatch
                  key={colorOption}
                  type="button"
                  color={colorOption}
                  selected={colorOption.toLowerCase() === color?.toLowerCase()}
                  onClick={() => setColor(colorOption)}
                  data-testid={`team-color-swatch-${colorOption.replace('#', '')}`}
                  aria-label={`Select team color ${colorOption}`}
                />
              ))}
            </PaletteContainer>
          </FormGroup>
        </form>
      </Modal.Content>

      <PageContent style={{ display: 'flex', justifyContent: 'flex-end', gap: 12, paddingTop: 0, paddingBottom: 16, paddingRight: 24, paddingLeft: 24 }}  >
        <StyledCancelButton onClick={onClose} disabled={isLoading} />
        <StyledSaveButton
          data-testid="team-save-button"
          label={isLoading ? 'Saving...' : (team ? 'Update Team' : 'Create Team')}
          onClick={() => handleSubmit()}
          disabled={isLoading || !name.trim()}
          width="140px"
        />
      </PageContent>
    </PositionedModal>
  );
};
